import React, { useState, useEffect } from 'react';
import {
  Snackbar as MuiSnackbar,
  Alert,
  IconButton,
  Slide,
  Fade,
  Grow,
  Box,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const SlideTransition = (props) => {
  return <Slide {...props} direction="up" />;
};

const GrowTransition = (props) => {
  return <Grow {...props} />;
};

const FadeTransition = (props) => {
  return <Fade {...props} />;
};

const Snackbar = ({
  open = false,
  message = 'This is a snackbar message',
  severity = 'info', // success, error, warning, info, none
  variant = 'filled', // filled, outlined, standard
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  autoHideDuration = 6000,
  showCloseButton = true,
  showIcon = true,
  transition = 'slide', // slide, fade, grow
  onClose,
  action,
  sx = {},
  ...props
}) => {
  const [internalOpen, setInternalOpen] = useState(open);

  useEffect(() => {
    setInternalOpen(open);
  }, [open]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setInternalOpen(false);
    if (onClose) {
      onClose(event, reason);
    }
  };

  const getTransitionComponent = () => {
    switch (transition) {
      case 'fade':
        return FadeTransition;
      case 'grow':
        return GrowTransition;
      case 'slide':
      default:
        return SlideTransition;
    }
  };

  const getIcon = () => {
    if (!showIcon) return null;
    
    switch (severity) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return null;
    }
  };

  const renderAction = () => {
    if (action) {
      return action;
    }
    
    if (showCloseButton) {
      return (
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={handleClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      );
    }
    
    return null;
  };

  // For severity-based snackbars, use Alert
  if (severity !== 'none') {
    return (
      <MuiSnackbar
        open={internalOpen}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        TransitionComponent={getTransitionComponent()}
        sx={sx}
        {...props}
      >
        <Alert
          onClose={showCloseButton ? handleClose : undefined}
          severity={severity}
          variant={variant}
          icon={showIcon ? undefined : false}
          sx={{
            width: '100%',
            minWidth: '300px',
            '& .MuiAlert-message': {
              padding: '0',
              display: 'flex',
              alignItems: 'center'
            }
          }}
        >
          {typeof message === 'string' ? (
            <Typography variant="body2">{message}</Typography>
          ) : (
            message
          )}
        </Alert>
      </MuiSnackbar>
    );
  }

  // For simple snackbars without severity
  return (
    <MuiSnackbar
      open={internalOpen}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      TransitionComponent={getTransitionComponent()}
      message={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon()}
          <Typography variant="body2" color="inherit">
            {message}
          </Typography>
        </Box>
      }
      action={renderAction()}
      sx={{
        '& .MuiSnackbarContent-root': {
          backgroundColor: '#323232',
          color: '#ffffff',
          minWidth: '300px',
          padding: '6px 16px',
          borderRadius: '4px'
        },
        ...sx
      }}
      {...props}
    />
  );
};

// Utility functions for quick snackbar usage
export const createSnackbar = (message, options = {}) => {
  return {
    message,
    open: true,
    ...options
  };
};

export const successSnackbar = (message, options = {}) => {
  return createSnackbar(message, { severity: 'success', ...options });
};

export const errorSnackbar = (message, options = {}) => {
  return createSnackbar(message, { severity: 'error', ...options });
};

export const warningSnackbar = (message, options = {}) => {
  return createSnackbar(message, { severity: 'warning', ...options });
};

export const infoSnackbar = (message, options = {}) => {
  return createSnackbar(message, { severity: 'info', ...options });
};

export default Snackbar;
